import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Container,
  Tooltip,
  IconButton,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUndo,
  faFilePdf,
  faInfoCircle,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";

import CourseService from "../../services/courseService";
import CourseDetails from "../../components/CourseDetails";
import CourseRoster from "../../components/Admin/CourseRoster";
import Course from "../../models/Course";
import useNotification from "../../hooks/useNotification";

interface ArchivedCoursesResponse {
  courses: Course[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

type Order = "asc" | "desc";

const ManageArchivedCourses: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isRosterOpen, setIsRosterOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCourses, setTotalCourses] = useState(0);
  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<keyof Course>("startDate");
  const [isRestoreDialogOpen, setIsRestoreDialogOpen] = useState(false);
  const [courseToRestore, setCourseToRestore] = useState<Course | null>(null);
  const { showNotification, hideNotification } = useNotification();

  const loadArchivedCourses = useCallback(async () => {
    try {
      const response: ArchivedCoursesResponse =
        await CourseService.fetchArchivedCourses(
          page + 1,
          rowsPerPage,
          orderBy,
          order
        );
      setCourses(response.courses);
      setTotalCourses(response.total);
    } catch (error) {
      console.error("Error fetching archived courses:", error);
      // Handle error (e.g., show error message to user)
    }
  }, [page, rowsPerPage, orderBy, order]);

  useEffect(() => {
    loadArchivedCourses();
  }, [loadArchivedCourses]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property: keyof Course) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleRestoreClick = (course: Course) => {
    setCourseToRestore(course);
    setIsRestoreDialogOpen(true);
  };

  const handleRestoreConfirm = async () => {
    if (courseToRestore) {
      try {
        await CourseService.restoreCourse(courseToRestore.id);
        setIsRestoreDialogOpen(false);
        setCourseToRestore(null);
        loadArchivedCourses();
        showNotification(
          `Course "${courseToRestore.name}" has been restored successfully`,
          "success"
        );
      } catch (error) {
        console.error("Error restoring course:", error);
        showNotification(
          `Failed to restore course "${courseToRestore.name}"`,
          "error"
        );
        // Handle error (e.g., show error message to user)
      }
    }
  };

  const handleRestoreCancel = () => {
    setIsRestoreDialogOpen(false);
    setCourseToRestore(null);
  };

  const handleDownloadPdf = useCallback((pdfUrl: string | null) => {
    if (pdfUrl) {
      window.open(pdfUrl, "_blank");
    } else {
      showNotification("PDF is not available for this course", "warning");
    }
  }, []);

  const handleViewDetails = (course: Course) => {
    setSelectedCourse(course);
    setIsDetailsOpen(true);
  };

  const handleViewRoster = (course: Course) => {
    setSelectedCourse(course);
    setIsRosterOpen(true);
  };

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Archived Courses
      </Typography>
      <Box sx={{ width: "100%" }}>
        <Paper sx={{ width: "100%", mb: 2 }}>
          <TableContainer>
            <Table
              sx={{ minWidth: 750 }}
              aria-labelledby="archivedCoursesTable"
            >
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "name"}
                      direction={orderBy === "name" ? order : "asc"}
                      onClick={() => handleRequestSort("name")}
                    >
                      Course Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "type"}
                      direction={orderBy === "type" ? order : "asc"}
                      onClick={() => handleRequestSort("type")}
                    >
                      Type
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "startDate"}
                      direction={orderBy === "startDate" ? order : "asc"}
                      onClick={() => handleRequestSort("startDate")}
                    >
                      Start Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "endDate"}
                      direction={orderBy === "endDate" ? order : "asc"}
                      onClick={() => handleRequestSort("endDate")}
                    >
                      End Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "location"}
                      direction={orderBy === "location" ? order : "asc"}
                      onClick={() => handleRequestSort("location")}
                    >
                      Location
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {courses.map((course) => (
                  <TableRow key={course.id}>
                    <TableCell>{course.name}</TableCell>
                    <TableCell>
                      {course.type
                        ? course.type.charAt(0).toUpperCase() +
                          course.type.slice(1).toLowerCase()
                        : ""}
                    </TableCell>
                    <TableCell>
                      {new Date(course.startDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {new Date(course.endDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{course.location}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title="Restore Course">
                          <IconButton
                            onClick={() => handleRestoreClick(course)}
                            size="small"
                          >
                            <FontAwesomeIcon icon={faUndo} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={
                            course.pdfFileUrl
                              ? "Download PDF"
                              : "PDF not available"
                          }
                        >
                          <span>
                            <IconButton
                              onClick={() =>
                                handleDownloadPdf(course.pdfFileUrl)
                              }
                              disabled={!course.pdfFileUrl}
                              size="small"
                              sx={{
                                color: course.pdfFileUrl
                                  ? "primary.main"
                                  : "action.disabled",
                                "&.Mui-disabled": {
                                  color: "action.disabled",
                                },
                              }}
                            >
                              <FontAwesomeIcon icon={faFilePdf} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="View Details">
                          <IconButton
                            onClick={() => handleViewDetails(course)}
                            size="small"
                          >
                            <FontAwesomeIcon icon={faInfoCircle} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Roster">
                          <IconButton
                            onClick={() => handleViewRoster(course)}
                            size="small"
                          >
                            <FontAwesomeIcon icon={faUsers} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalCourses}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>

        <Dialog open={isDetailsOpen} onClose={() => setIsDetailsOpen(false)}>
          <DialogTitle>Course Details</DialogTitle>
          <DialogContent>
            {selectedCourse && <CourseDetails course={selectedCourse} />}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsDetailsOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={isRosterOpen}
          onClose={() => setIsRosterOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Course Roster</DialogTitle>
          <DialogContent sx={{ maxHeight: "70vh", overflow: "auto" }}>
            {selectedCourse && (
              <CourseRoster
                course={selectedCourse}
                onParticipantViewChange={() => {}}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsRosterOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
        {/* Restore Confirmation Dialog */}
        <Dialog
          open={isRestoreDialogOpen}
          onClose={handleRestoreCancel}
          aria-labelledby="restore-dialog-title"
          aria-describedby="restore-dialog-description"
        >
          <DialogTitle id="restore-dialog-title">
            Confirm Course Restoration
          </DialogTitle>
          <DialogContent>
            <Typography id="restore-dialog-description">
              Are you sure you want to restore the course "
              {courseToRestore?.name}"? This will make the course active and
              visible again.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleRestoreCancel} color="primary">
              Cancel
            </Button>
            <Button
              onClick={handleRestoreConfirm}
              color="primary"
              variant="contained"
            >
              Restore
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default ManageArchivedCourses;
